import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAdminFetchApi from '../../utils/adminFetchApi';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';

export default function AdminProfileEditPage() {
  const { uuid } = useParams();
  const api = useAdminFetchApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await api.get(`/admin/profiles/${uuid}`);
        const payload = data?.data || data;
        if (mounted) {
          setUser(payload);
          setForm({ ...payload });
        }
      } catch (e) {
        try {
          // fallback to non-admin profile endpoint if needed
          const data = await api.get(`/profile/user/${uuid}`);
          if (mounted) {
            setUser(data);
            setForm({ ...data });
          }
        } catch (e2) {
          setError(e2.message || 'Failed to load user');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [uuid, api]);

  // Load channels for dropdown
  useEffect(() => {
    let mounted = true;
    const loadChannels = async () => {
      try {
        setLoadingChannels(true);
        const data = await api.get('/admin/channels');
        const list = Array.isArray(data) ? data : (data?.channels || []);
        if (!mounted) return;
        setChannels(list);
      } catch (e) {
        // silent fail; will still show current value
      } finally {
        if (mounted) setLoadingChannels(false);
      }
    };
    loadChannels();
    return () => { mounted = false; };
  }, [api]);

  const mainImage = useMemo(() => form?.images?.[0]?.image_path || '', [form]);
  const thumbImage = useMemo(() => form?.images?.[0]?.thumb_path || form?.images?.[0]?.image_path || '', [form]);
  const dateOfBirthValue = useMemo(() => {
    const v = form?.date_of_birth;
    if (!v) return '';
    return String(v).slice(0, 10);
  }, [form?.date_of_birth]);
  const unlockDateValue = useMemo(() => {
    const v = form?.unlock_date;
    if (!v) return '';
    return String(v).slice(0, 10);
  }, [form?.unlock_date]);
  const createdAtLabel = useMemo(() => {
    if (!form?.created_at) return '';
    const d = new Date(form.created_at);
    return isNaN(d.getTime()) ? String(form.created_at) : d.toLocaleString();
  }, [form?.created_at]);
  const updatedAtLabel = useMemo(() => {
    if (!form?.updated_at) return '';
    const d = new Date(form.updated_at);
    return isNaN(d.getTime()) ? String(form.updated_at) : d.toLocaleString();
  }, [form?.updated_at]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const num = value === '' ? '' : Number(value);
    setForm(prev => ({ ...prev, [name]: isNaN(num) ? prev[name] : num }));
  };
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value || null }));
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    const id = form.uuid || uuid;
    const fd = new FormData();
    fd.append('image', file);
    try {
      if (kind === 'image') setUploadingImage(true); else setUploadingThumb(true);
      let endpoint = kind === 'image' ? `/admin/profiles/${id}/image` : `/admin/profiles/${id}/thumbnail`;
      let resp;
      try {
        resp = await api.post(endpoint, fd);
      } catch (e) {
        endpoint = kind === 'image' ? `/profile/${id}/image` : `/profile/${id}/thumbnail`;
        resp = await api.post(endpoint, fd);
      }
      let newImage = null;
      if (resp?.image) newImage = resp.image;
      else if (Array.isArray(resp?.images) && resp.images[0]) newImage = resp.images[0];
      else if (resp?.data?.image) newImage = resp.data.image;
      if (newImage) {
        setForm(prev => ({
          ...prev,
          images: [{
            ...(prev.images?.[0] || {}),
            ...newImage,
            image_path: newImage.image_path || prev.images?.[0]?.image_path,
            thumb_path: kind === 'thumbnail' ? (newImage.thumb_path || newImage.image_path || prev.images?.[0]?.thumb_path) : (prev.images?.[0]?.thumb_path || newImage.thumb_path || newImage.image_path)
          }]
        }));
      }
    } finally {
      if (kind === 'image') setUploadingImage(false); else setUploadingThumb(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const id = form.uuid || uuid;
      const payload = {
        uuid: id,
        name: form.name ?? '',
        nickname: form.nickname ?? '',
        country: form.country ?? '',
        channel: form.channel ?? '',
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
  profile_type: (form.profile_type === '' || form.profile_type == null) ? null : Number(form.profile_type),
        priority: (form.priority === '' || form.priority == null) ? null : Number(form.priority),
        email: form.email ?? '',
        message: form.message ?? '',
        is_public: !!form.is_public,
        is_enabled: form.is_enabled !== undefined ? !!form.is_enabled : undefined,
        unlock_date: form.unlock_date || null,
        buy_price_usd: form.buy_price_usd === '' ? null : form.buy_price_usd,
        spot_number: form.spot_number === '' ? null : form.spot_number,
        state: form.state === '' ? null : form.state,
        created_at: form.created_at || undefined,
        updated_at: form.updated_at || undefined,
      };
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      await api.put(`/admin/profile`, payload);
      navigate('/admin/profiles');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-section"><div className="loading-row">Loading…</div></div>;
  if (error) return <div className="admin-section"><div className="error-message">{error}</div></div>;
  if (!user) return null;

  return (
    <div className="admin-section">
      <div className="admin-toolbar sticky">
        <button className="button" onClick={() => navigate(-1)}>← Back</button>
        <div className="spacer" />
        <button className="button primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>

      <div className="admin-edit-grid">
        <div className="panel">
          <h3>Profile</h3>
          <div className="grid-2">
            <div className="field"><label>UUID</label><input value={form.uuid || ''} readOnly /></div>
            <div className="field"><label>Email</label><input name="email" value={form.email || ''} onChange={handleChange} /></div>
            <div className="field"><label>Name</label><input name="name" value={form.name || ''} onChange={handleChange} /></div>
            <div className="field"><label>Nickname</label><input name="nickname" value={form.nickname || ''} onChange={handleChange} /></div>
            <div className="field"><label>Country</label><input name="country" value={form.country || ''} onChange={handleChange} /></div>
            <div className="field"><label>Channel</label>
              <select name="channel" value={form.channel || ''} onChange={handleChange}>
                {/* Preserve current value if not present in list */}
                {(!channels || channels.length === 0) && (
                  <option value={form.channel || ''}>{form.channel || 'default'}</option>
                )}
                {channels && channels.length > 0 && (
                  <>
                    {/* If current value not in list, include it at top */}
                    {form.channel && !channels.some(c => (c.key || c.id || c.name || c) === form.channel) && (
                      <option value={form.channel}>{form.channel}</option>
                    )}
                    {channels.map((c, idx) => {
                      const value = c?.key || c?.id || c?.name || String(c);
                      const label = c?.label || c?.name || c?.id || String(value);
                      return <option key={value + '-' + idx} value={value}>{label}</option>;
                    })}
                  </>
                )}
              </select>
            </div>
            <div className="field"><label>Date of Birth</label><input type="date" name="date_of_birth" value={dateOfBirthValue} onChange={handleDateChange} /></div>
            <div className="field"><label>Gender</label>
              <select name="gender" value={form.gender || ''} onChange={handleChange}>
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="field"><label>Profile Type</label>
              <select name="profile_type" value={(form.profile_type ?? '') + ''} onChange={handleChange}>
                <option value="">—</option>
                <option value="1">Couple</option>
                <option value="2">Father with childs</option>
                <option value="3">Mother with childs</option>
                <option value="4">Full family</option>
                <option value="5">Friends</option>
              </select>
            </div>
            <div className="field"><label>Priority</label><input type="number" name="priority" value={form.priority ?? ''} onChange={handleNumberChange} placeholder="—" /></div>
            <div className="field span-2"><label>Message</label><textarea name="message" value={form.message || ''} onChange={handleChange} rows={4} /></div>
            <div className="field inline"><label>Public</label><input type="checkbox" name="is_public" checked={!!form.is_public} onChange={handleChange} /></div>
            <div className="field inline"><label>Enabled</label><input type="checkbox" name="is_enabled" checked={!!form.is_enabled} onChange={handleChange} /></div>
            <div className="field"><label>Unlock date</label><input type="date" name="unlock_date" value={unlockDateValue} onChange={handleDateChange} /></div>
            <div className="field"><label>Buy price (USD)</label><input type="number" step="0.01" name="buy_price_usd" value={form.buy_price_usd ?? ''} onChange={handleNumberChange} /></div>
            <div className="field"><label>Spot number</label><input type="number" name="spot_number" value={form.spot_number ?? ''} onChange={handleNumberChange} /></div>
            <div className="field"><label>State</label><input type="number" name="state" value={form.state ?? ''} onChange={handleNumberChange} /></div>
            <div className="field"><label>View count</label><input value={form.view_count ?? 0} readOnly /></div>
            <div className="field"><label>Created at</label><input value={createdAtLabel} readOnly /></div>
            <div className="field"><label>Updated at</label><input value={updatedAtLabel} readOnly /></div>
          </div>
        </div>

        <div className="panel">
          <h3>Images</h3>
          <div className="images-block">
            <div className="image-item">
              <div className="preview"><img src={getImageUrl(mainImage)} alt="Profile" onError={(e)=>handleImageError(e)} /></div>
              <div className="actions"><label className="button">{uploadingImage ? 'Uploading…' : 'Change image'}<input type="file" accept="image/*" onChange={(e)=>{const f=e.target.files?.[0]; if(f) uploadFile(f,'image'); e.target.value='';}} style={{display:'none'}}/></label></div>
            </div>
            <div className="image-item">
              <div className="preview"><img src={getImageUrl(thumbImage)} alt="Thumbnail" onError={(e)=>handleImageError(e)} /></div>
              <div className="actions"><label className="button">{uploadingThumb ? 'Uploading…' : 'Change thumbnail'}<input type="file" accept="image/*" onChange={(e)=>{const f=e.target.files?.[0]; if(f) uploadFile(f,'thumbnail'); e.target.value='';}} style={{display:'none'}}/></label></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
